import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import Layout from '../components/Layout';
import AutoComplete from '../components/AutoComplete';
import DataTable from '../components/DataTable';
import Alert from '../components/Alert';
import { useAuth } from '../context/AuthContext';
import {
  guiaRemisionService, almacenService, trabajadorService,
  serieService, tablaService,
} from '../services/api';
import { getErrorDetail } from '../utils/errorHelper';
import '../styles/requerimientos.css';
import '../styles/ordenes_compra.css';
import '../styles/datatable.css';

const today   = () => new Date().toISOString().split('T')[0];
const weekAgo = () => { const d = new Date(); d.setDate(d.getDate() - 6); return d.toISOString().split('T')[0]; };
const fmtN    = (v, d = 4) => (parseFloat(v) || 0).toLocaleString('es-PE', { minimumFractionDigits: d, maximumFractionDigits: d });
const fmtNum  = (serie, numero, pad = 6) => `${(serie || '').trim()}-${String(numero || 0).padStart(pad, '0')}`;

const ESTADO_RQ_CFG = {
  'PENDIENTE': { color: '#60a5fa', bg: 'rgba(96,165,250,0.12)', border: 'rgba(96,165,250,0.3)'  },
  'APROBADO':  { color: '#22c55e', bg: 'rgba(34,197,94,0.12)',  border: 'rgba(34,197,94,0.3)'   },
  'RECHAZADO': { color: '#f87171', bg: 'rgba(239,68,68,0.12)',  border: 'rgba(239,68,68,0.3)'   },
};
const ESTADO_GR_CFG = {
  'GENERADO': { color: '#22c55e', bg: 'rgba(34,197,94,0.12)', border: 'rgba(34,197,94,0.3)' },
  'ANULADO':  { color: '#94a3b8', bg: 'rgba(148,163,184,0.1)', border: 'rgba(148,163,184,0.25)' },
};

const Badge = ({ label, cfg }) => {
  const c = cfg || { color: '#94a3b8', bg: 'rgba(148,163,184,0.1)', border: 'rgba(148,163,184,0.2)' };
  return (
    <span style={{ background: c.bg, color: c.color, border: `1px solid ${c.border}`, borderRadius: 10, padding: '2px 9px', fontSize: 10.5, fontWeight: 700, whiteSpace: 'nowrap' }}>
      {label}
    </span>
  );
};

const inputStyle = {
  height: 30, padding: '0 9px', fontSize: 12, borderRadius: 7, boxSizing: 'border-box',
  background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.09)',
  color: '#e2e8f0', outline: 'none', fontFamily: 'Inter, sans-serif', width: '100%',
};
const selectStyle = {
  height: 30, padding: '0 8px', fontSize: 12, borderRadius: 7, boxSizing: 'border-box',
  background: 'rgba(15,27,45,0.9)', border: '1px solid rgba(255,255,255,0.11)',
  color: '#e2e8f0', outline: 'none', fontFamily: 'Inter, sans-serif', width: '100%', cursor: 'pointer',
};

export default function GrDesdeRq() {
  const { user, sucursal: sesionSucursal } = useAuth();
  const [view, setView] = useState('list'); // 'list' | 'form'

  /* ── Lista de RQs ── */
  const [rqsLista,    setRqsLista]    = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [alert,       setAlert]       = useState(null);

  /* ── Filtros ── */
  const [fNumero,    setFNumero]    = useState('');
  const [fArea,      setFArea]      = useState('');
  const [fSolicitante, setFSolicitante] = useState('');
  const [fFechaIni,  setFFechaIni]  = useState(weekAgo);
  const [fFechaFin,  setFFechaFin]  = useState(today);

  /* ── Catálogos ── */
  const [almacenes,      setAlmacenes]      = useState([]);
  const [series,         setSeries]         = useState([]);
  const [trabajadores,   setTrabajadores]   = useState([]);
  const [motivoTraslado, setMotivoTraslado] = useState([]);
  const [loadingCat,     setLoadingCat]     = useState(false);
  const catLoaded = useRef(false);

  /* ── RQ seleccionado ── */
  const [rqSeleccionado, setRqSeleccionado] = useState(null);
  const [rqItems,        setRqItems]        = useState([]);
  const [loadingItems,   setLoadingItems]   = useState(false);
  const [seleccionados,  setSeleccionados]  = useState({});
  const [soloPendientes, setSoloPendientes] = useState(false);

  /* ── Formulario GR ── */
  const [form, setForm] = useState({
    tipo_guia:          'REMITENTE',
    id_serie:           '',
    fecha_guia:         today(),
    id_almacen:         '',
    id_almacen_destino: '',
    fecha_traslado:     '',
    fecha_entrega:      '',
    id_solicitante:     '',
    id_motivo_traslado: '',
    observacion:        '',
  });
  const [saving, setSaving] = useState(false);
  const [creado, setCreado] = useState(null);

  const mtSvc = useRef(tablaService('motivos-traslado'));

  /* ── Carga lista RQs ── */
  const fetchRqsLista = useCallback(async () => {
    setLoadingList(true);
    try {
      const r = await guiaRemisionService.getRqsParaGr();
      setRqsLista(r.data || []);
    } catch {
      setAlert({ type: 'error', message: 'Error al cargar los requerimientos.' });
    } finally {
      setLoadingList(false);
    }
  }, []);

  /* ── Carga catálogos ── */
  const fetchCatalogos = useCallback(async () => {
    if (catLoaded.current) return;
    setLoadingCat(true);
    try {
      const [alm, ser, trab, mt] = await Promise.all([
        almacenService.getAll(),
        serieService.getAll('GR'),
        trabajadorService.getAll(),
        mtSvc.current.getAll(),
      ]);
      setAlmacenes(alm.data || []);
      const serData = ser.data || [];
      if (serData.length > 0) {
        setSeries(serData);
      } else {
        const serAll = await serieService.getAll();
        setSeries(serAll.data || []);
      }
      setTrabajadores(trab.data || []);
      setMotivoTraslado(mt.data || []);
      catLoaded.current = true;
    } catch (err) {
      setAlert({ type: 'error', message: getErrorDetail(err, 'Error al cargar catálogos.') });
    } finally {
      setLoadingCat(false);
    }
  }, []);

  useEffect(() => {
    fetchRqsLista();
  }, [fetchRqsLista]);

  /* ── Opciones de filtro derivadas ── */
  const almacenesSucursal = useMemo(
    () => sesionSucursal?.id_sucursal
      ? almacenes.filter(a => String(a.id_sucursal) === String(sesionSucursal.id_sucursal))
      : almacenes,
    [almacenes, sesionSucursal]
  );

  const areasRq = useMemo(
    () => [...new Set(rqsLista.map(r => r.area_nombre).filter(Boolean))].sort(),
    [rqsLista]
  );

  /* ── Filtrado lista ── */
  const rqsFiltrados = useMemo(() => {
    return rqsLista.filter(rq => {
      if (fNumero     && !String(rq.numero).includes(fNumero)) return false;
      if (fArea       && rq.area_nombre !== fArea) return false;
      if (fSolicitante && !(rq.solicitante_nombre || '').toLowerCase().includes(fSolicitante.toLowerCase())) return false;
      const fecha = (rq.fecha_solicitud || '').slice(0, 10);
      if (fFechaIni && fecha < fFechaIni) return false;
      if (fFechaFin && fecha > fFechaFin) return false;
      return true;
    });
  }, [rqsLista, fNumero, fArea, fSolicitante, fFechaIni, fFechaFin]);

  /* ── Seleccionar RQ ── */
  const seleccionarRq = async (rq) => {
    fetchCatalogos();
    setRqSeleccionado(rq);
    setSeleccionados({});
    setRqItems([]);
    setSoloPendientes(false);
    setForm({
      tipo_guia:          'REMITENTE',
      id_serie:           '',
      fecha_guia:         today(),
      id_almacen:         '',
      id_almacen_destino: '',
      fecha_traslado:     '',
      fecha_entrega:      '',
      id_solicitante:     rq.id_solicitante ? String(rq.id_solicitante) : '',
      id_motivo_traslado: '',
      observacion:        '',
    });
    setCreado(null);
    setView('form');
    setLoadingItems(true);
    try {
      const res = await guiaRemisionService.getRqItems(rq.id_requerimiento);
      setRqItems(res.data || []);
    } catch (err) {
      setAlert({ type: 'error', message: getErrorDetail(err, 'Error al cargar ítems del requerimiento.') });
    } finally {
      setLoadingItems(false);
    }
  };

  /* ── Ítems visibles ── */
  const itemsVisibles = useMemo(() => {
    if (soloPendientes) return rqItems.filter(it => (parseFloat(it.cantidad_pendiente) || 0) > 0);
    return rqItems;
  }, [rqItems, soloPendientes]);

  /* ── Selección ── */
  const toggleItem = (it) => {
    const key = it.id_detalle;
    setSeleccionados(prev => {
      if (key in prev) { const n = { ...prev }; delete n[key]; return n; }
      const pend = parseFloat(it.cantidad_pendiente) || 0;
      return { ...prev, [key]: pend > 0 ? pend : parseFloat(it.cantidad) || 1 };
    });
  };

  const setCantidadItem = (id_detalle, val) =>
    setSeleccionados(prev => ({ ...prev, [id_detalle]: val }));

  const aptosPendientes = useMemo(
    () => itemsVisibles.filter(it => (parseFloat(it.cantidad_pendiente) || 0) > 0),
    [itemsVisibles]
  );
  const todosSeleccionados = aptosPendientes.length > 0 && aptosPendientes.every(it => it.id_detalle in seleccionados);

  const toggleAll = () => {
    if (todosSeleccionados) {
      setSeleccionados(prev => {
        const n = { ...prev };
        aptosPendientes.forEach(it => delete n[it.id_detalle]);
        return n;
      });
    } else {
      const add = {};
      aptosPendientes.forEach(it => { if (!(it.id_detalle in seleccionados)) add[it.id_detalle] = parseFloat(it.cantidad_pendiente) || 1; });
      setSeleccionados(prev => ({ ...prev, ...add }));
    }
  };

  const nSel = Object.keys(seleccionados).length;
  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const n    = v => (v !== '' && v !== null && v !== undefined) ? Number(v) : null;

  /* ── Guardar GR ── */
  const handleSubmit = async () => {
    if (!form.id_almacen)   { setAlert({ type: 'warning', message: 'Seleccione un almacén de origen.' }); return; }
    if (!form.fecha_guia)   { setAlert({ type: 'warning', message: 'Ingrese la fecha de guía.' }); return; }
    const itemsSel = rqItems.filter(it => it.id_detalle in seleccionados);
    if (itemsSel.length === 0) { setAlert({ type: 'warning', message: 'Seleccione al menos un artículo.' }); return; }
    for (const it of itemsSel) {
      const qty       = parseFloat(seleccionados[it.id_detalle]) || 0;
      const pendiente = parseFloat(it.cantidad_pendiente) || 0;
      if (qty <= 0)        { setAlert({ type: 'warning', message: `Cantidad de "${it.articulo_descripcion}" debe ser mayor a 0.` }); return; }
      if (qty > pendiente) { setAlert({ type: 'warning', message: `Cantidad de "${it.articulo_descripcion}" excede la pendiente (${fmtN(pendiente, 4)}).` }); return; }
    }

    const payload = {
      tipo_guia:           form.tipo_guia,
      fecha_guia:          form.fecha_guia,
      id_almacen:          n(form.id_almacen),
      id_sucursal:         user?.id_sucursal ? n(user.id_sucursal) : null,
      id_serie:            n(form.id_serie),
      id_requerimiento:    rqSeleccionado.id_requerimiento,
      id_almacen_destino:  n(form.id_almacen_destino),
      fecha_traslado:      form.fecha_traslado || null,
      fecha_entrega:       form.fecha_entrega  || null,
      id_solicitante:      n(form.id_solicitante),
      id_motivo_traslado:  n(form.id_motivo_traslado),
      observacion:         form.observacion || null,
      detalle: itemsSel.map((it, i) => ({
        orden:          i + 1,
        id_articulo:    it.id_articulo,
        id_unidad:      it.id_unidad || null,
        cantidad:       parseFloat(seleccionados[it.id_detalle]) || 1,
        id_detalle_rq:  it.id_detalle,
      })),
    };

    setSaving(true);
    try {
      const res = await guiaRemisionService.create(payload);
      setCreado(res.data);
      fetchRqsLista();
    } catch (err) {
      setAlert({ type: 'error', message: getErrorDetail(err, 'Error al generar la guía de remisión.') });
    } finally {
      setSaving(false);
    }
  };

  /* ════════════════════════════════════════════════════════════════
     COLUMNAS LISTA RQs
  ════════════════════════════════════════════════════════════════ */
  const tableColumns = [
    {
      key: 'numero', label: 'Número RQ', width: 110,
      render: r => (
        <span style={{ fontWeight: 700, color: '#60a5fa', fontSize: 12, fontFamily: 'monospace' }}>
          {fmtNum(r.serie_codigo, r.numero)}
        </span>
      ),
    },
    {
      key: 'fecha_solicitud', label: 'Fecha Solicitud', width: 110,
      render: r => <span style={{ fontSize: 11 }}>{(r.fecha_solicitud || '').slice(0, 10) || '—'}</span>,
    },
    { key: 'area_nombre', label: 'Área', style: { fontSize: 11 } },
    {
      key: 'solicitante_nombre', label: 'Solicitante',
      render: r => <span style={{ fontSize: 11 }}>{r.solicitante_nombre || '—'}</span>,
    },
    {
      key: 'estado', label: 'Estado', width: 100,
      render: r => {
        const cfg = ESTADO_RQ_CFG[r.estado] || { color: '#94a3b8', bg: 'rgba(148,163,184,0.1)', border: 'rgba(148,163,184,0.2)' };
        return <Badge label={r.estado || '—'} cfg={cfg} />;
      },
    },
    {
      key: '_accion', label: 'Acción', width: 100,
      render: r => (
        <button
          onClick={() => seleccionarRq(r)}
          style={{
            fontSize: 10.5, fontWeight: 600, padding: '3px 8px', borderRadius: 6,
            border: '1px solid rgba(96,165,250,0.35)', cursor: 'pointer',
            background: 'rgba(96,165,250,0.12)', color: '#60a5fa',
            transition: 'all 0.15s', whiteSpace: 'nowrap',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(96,165,250,0.22)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(96,165,250,0.12)'; }}
        >
          Generar GR
        </button>
      ),
    },
  ];

  /* ════════════════════════════════════════════════════════════════
     PANTALLA DE ÉXITO
  ════════════════════════════════════════════════════════════════ */
  if (creado) {
    const ec = ESTADO_GR_CFG[creado.estado] || {};
    return (
      <Layout>
        <div style={{ maxWidth: 520, margin: '80px auto', textAlign: 'center', padding: '0 20px' }}>
          <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(34,197,94,0.1)', border: '2px solid rgba(34,197,94,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <div style={{ fontSize: 13, color: '#64748b', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 600, marginBottom: 8 }}>
            Guía de Remisión generada
          </div>
          <div style={{ fontSize: 32, fontWeight: 800, color: '#60a5fa', letterSpacing: 2, marginBottom: 6 }}>
            {fmtNum(creado.serie_codigo, creado.numero)}
          </div>
          <div style={{ fontSize: 12.5, color: '#64748b', marginBottom: 4 }}>
            {creado.almacen_nombre} · {(creado.fecha_guia || '').slice(0, 10)}
          </div>
          <div style={{ fontSize: 11, color: '#475569', marginBottom: 20 }}>
            desde RQ-{String(rqSeleccionado?.numero || 0).padStart(6, '0')}
          </div>
          <Badge label={creado.estado || 'GENERADO'} cfg={ec} />
          <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 36 }}>
            <button className="btn btn-secondary" onClick={() => setView('list')}>Ver lista de RQs</button>
            <button className="btn btn-primary" onClick={() => { setCreado(null); seleccionarRq(rqSeleccionado); }}>
              + Nueva GR desde mismo RQ
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  /* ════════════════════════════════════════════════════════════════
     VISTA LISTA
  ════════════════════════════════════════════════════════════════ */
  if (view === 'list') {
    return (
      <Layout>
        {alert && <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />}
        <div style={{ padding: '0 0 16px' }}>
          <div className="oc-page-header">
            <h2>GR desde Requerimiento</h2>
            <div className="oc-header-actions">
              <button className="btn btn-secondary btn-sm" onClick={fetchRqsLista}>↺ Actualizar</button>
            </div>
          </div>

          {/* ── Filtros ── */}
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 1.2fr 1.2fr 1fr 1fr auto',
            gap: 10, marginBottom: 10, alignItems: 'end',
            background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)',
            borderRadius: 10, padding: '12px 16px',
          }}>
            <div>
              <label style={{ fontSize: 10, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.6px', display: 'block', marginBottom: 4 }}>Número RQ</label>
              <input value={fNumero} onChange={e => setFNumero(e.target.value)} placeholder="Buscar..." style={inputStyle}
                onFocus={e => e.target.style.borderColor = '#3b82f6'} onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.09)'} />
            </div>
            <div>
              <label style={{ fontSize: 10, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.6px', display: 'block', marginBottom: 4 }}>Área</label>
              <select value={fArea} onChange={e => setFArea(e.target.value)} style={selectStyle}>
                <option value="">Todas</option>
                {areasRq.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 10, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.6px', display: 'block', marginBottom: 4 }}>Solicitante</label>
              <input value={fSolicitante} onChange={e => setFSolicitante(e.target.value)} placeholder="Buscar solicitante..." style={inputStyle}
                onFocus={e => e.target.style.borderColor = '#3b82f6'} onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.09)'} />
            </div>
            <div>
              <label style={{ fontSize: 10, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.6px', display: 'block', marginBottom: 4 }}>Fecha desde</label>
              <input type="date" value={fFechaIni} onChange={e => setFFechaIni(e.target.value)} style={inputStyle}
                onFocus={e => e.target.style.borderColor = '#3b82f6'} onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.09)'} />
            </div>
            <div>
              <label style={{ fontSize: 10, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.6px', display: 'block', marginBottom: 4 }}>Fecha hasta</label>
              <input type="date" value={fFechaFin} onChange={e => setFFechaFin(e.target.value)} style={inputStyle}
                onFocus={e => e.target.style.borderColor = '#3b82f6'} onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.09)'} />
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <button
                onClick={() => { setFNumero(''); setFArea(''); setFSolicitante(''); setFFechaIni(weekAgo()); setFFechaFin(today()); }}
                style={{ height: 30, padding: '0 12px', fontSize: 11, fontWeight: 600, borderRadius: 7, border: '1px solid rgba(255,255,255,0.07)', cursor: 'pointer', background: 'rgba(255,255,255,0.03)', color: '#64748b', fontFamily: 'Inter, sans-serif' }}
              >✕ Limpiar</button>
            </div>
          </div>

          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 6, paddingLeft: 2 }}>
            {rqsFiltrados.length} resultado{rqsFiltrados.length !== 1 ? 's' : ''} de {rqsLista.length} requerimientos
          </div>

          <DataTable columns={tableColumns} data={rqsFiltrados} loading={loadingList} hideSearch hideToolbarRight />
        </div>
      </Layout>
    );
  }

  /* ════════════════════════════════════════════════════════════════
     VISTA FORMULARIO GR
  ════════════════════════════════════════════════════════════════ */
  const almacenesDestino = almacenes.map(a => ({
    ...a,
    display_destino: `${a.nombre}${a.direccion ? ' | ' + a.direccion : ''}${a.ubigeo ? ' [' + a.ubigeo + ']' : ''}`,
  }));

  return (
    <Layout>
      {alert && <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />}

      <div className="rq-page">
        {/* ── Header ── */}
        <div className="rq-header">
          <div className="rq-header-left">
            <button className="oc-back-btn" onClick={() => setView('list')}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <div className="rq-header-title">
              <div className="rq-header-meta">Almacén — GR desde Requerimiento</div>
              <div className="rq-header-name">
                {rqSeleccionado ? `RQ-${String(rqSeleccionado.numero).padStart(6, '0')}` : 'Generar Guía de Remisión'}
              </div>
            </div>
          </div>
        </div>

        {/* ── Body ── */}
        <div className="rq-body" style={{ overflowY: 'auto' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>

            {/* ── SECCIÓN 1: Info del RQ origen ── */}
            {rqSeleccionado && (
              <div className="rq-card">
                <div className="rq-card-title">Requerimiento origen</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '0 14px' }}>
                  <div className="form-row">
                    <label>Número RQ</label>
                    <input value={`RQ-${String(rqSeleccionado.numero).padStart(6, '0')}`} readOnly style={{ opacity: 0.75, cursor: 'default', fontWeight: 700, color: '#60a5fa', ...inputStyle }} />
                  </div>
                  <div className="form-row">
                    <label>Área</label>
                    <input value={rqSeleccionado.area_nombre || ''} readOnly style={{ opacity: 0.65, cursor: 'default', ...inputStyle }} />
                  </div>
                  <div className="form-row">
                    <label>Solicitante</label>
                    <input value={rqSeleccionado.solicitante_nombre || ''} readOnly style={{ opacity: 0.65, cursor: 'default', ...inputStyle }} />
                  </div>
                  <div className="form-row">
                    <label>Fecha Solicitud</label>
                    <input value={(rqSeleccionado.fecha_solicitud || '').slice(0, 10) || ''} readOnly style={{ opacity: 0.65, cursor: 'default', ...inputStyle }} />
                  </div>
                </div>
              </div>
            )}

            {/* ── SECCIÓN 2: Cabecera GR ── */}
            <div className="rq-card">
              <div className="rq-card-title">Datos de la Guía de Remisión</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '0 14px', alignItems: 'start' }}>
                <div className="form-row">
                  <label>Tipo de Guía *</label>
                  <select value={form.tipo_guia} onChange={e => setF('tipo_guia', e.target.value)} style={selectStyle}>
                    <option value="REMITENTE">REMITENTE</option>
                    <option value="TRANSPORTISTA">TRANSPORTISTA</option>
                  </select>
                </div>
                <AutoComplete
                  label="Serie"
                  value={String(form.id_serie || '')}
                  options={series} idField="id_serie"
                  displayField={s => `${s.serie} - ${s.descripcion || ''}`}
                  onChange={v => setF('id_serie', v)}
                  placeholder="Seleccionar serie..."
                />
                <div className="form-row">
                  <label>Fecha de Guía *</label>
                  <input type="date" value={form.fecha_guia} onChange={e => setF('fecha_guia', e.target.value)} style={inputStyle} />
                </div>
                <AutoComplete
                  label="Almacén *"
                  value={String(form.id_almacen || '')}
                  options={almacenesSucursal} idField="id_almacen" displayField="nombre"
                  onChange={v => setF('id_almacen', v)}
                  placeholder="Seleccionar almacén origen..."
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '0 14px', alignItems: 'start', marginTop: 4 }}>
                <AutoComplete
                  label="Almacén Destino"
                  value={String(form.id_almacen_destino || '')}
                  options={almacenesDestino} idField="id_almacen" displayField="display_destino"
                  onChange={v => setF('id_almacen_destino', v)}
                  placeholder="Buscar almacén destino..."
                  dropdownMinWidth={420}
                />
                <div className="form-row">
                  <label>Fecha de Traslado</label>
                  <input type="date" value={form.fecha_traslado} onChange={e => setF('fecha_traslado', e.target.value)} style={inputStyle} />
                </div>
                <div className="form-row">
                  <label>Fecha de Entrega</label>
                  <input type="date" value={form.fecha_entrega} onChange={e => setF('fecha_entrega', e.target.value)} style={inputStyle} />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr', gap: '0 14px', alignItems: 'start', marginTop: 4 }}>
                <AutoComplete
                  label="Solicitante"
                  value={String(form.id_solicitante || '')}
                  options={trabajadores} idField="id_trabajador" displayField="nombres"
                  onChange={v => setF('id_solicitante', v)}
                  placeholder="Buscar trabajador..."
                  twoLine codeField="dni"
                />
                <AutoComplete
                  label="Motivo de Traslado"
                  value={String(form.id_motivo_traslado || '')}
                  options={motivoTraslado} idField="id_motivo_traslado"
                  displayField={m => `${m.codigo} - ${m.descripcion}`}
                  onChange={v => setF('id_motivo_traslado', v)}
                  placeholder="Seleccionar motivo..."
                />
                <div className="form-row" style={{ display: 'flex', flexDirection: 'column' }}>
                  <label>Observaciones</label>
                  <textarea value={form.observacion} onChange={e => setF('observacion', e.target.value)}
                    rows={2} placeholder="Observaciones..."
                    style={{ flex: 1, width: '100%', boxSizing: 'border-box', padding: '6px 10px', fontSize: 12.5, borderRadius: 8, resize: 'vertical', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.11)', color: '#e2e8f0', fontFamily: 'Inter, sans-serif', outline: 'none', minHeight: 30 }}
                    onFocus={e => { e.target.style.borderColor = '#3b82f6'; e.target.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.15)'; }}
                    onBlur={e  => { e.target.style.borderColor = 'rgba(255,255,255,0.11)'; e.target.style.boxShadow = 'none'; }}
                  />
                </div>
              </div>
            </div>

            {/* ── SECCIÓN 3: Ítems del RQ ── */}
            <div className="rq-card" style={{ flex: 'none' }}>
              <div className="rq-table-header">
                <div className="rq-table-header-left">
                  <span className="rq-table-title">Artículos del Requerimiento</span>
                  <div style={{ display: 'flex', gap: 4, marginLeft: 8 }}>
                    <button onClick={() => setSoloPendientes(false)} style={{ fontSize: 11, padding: '2px 9px', borderRadius: 5, border: '1px solid', cursor: 'pointer', fontWeight: 600, transition: 'all 0.15s', background: !soloPendientes ? 'rgba(96,165,250,0.15)' : 'rgba(255,255,255,0.04)', color: !soloPendientes ? '#60a5fa' : '#64748b', borderColor: !soloPendientes ? 'rgba(96,165,250,0.4)' : 'rgba(255,255,255,0.1)' }}>Todos</button>
                    <button onClick={() => setSoloPendientes(true)}  style={{ fontSize: 11, padding: '2px 9px', borderRadius: 5, border: '1px solid', cursor: 'pointer', fontWeight: 600, transition: 'all 0.15s', background:  soloPendientes ? 'rgba(96,165,250,0.15)' : 'rgba(255,255,255,0.04)', color:  soloPendientes ? '#60a5fa' : '#64748b', borderColor:  soloPendientes ? 'rgba(96,165,250,0.4)' : 'rgba(255,255,255,0.1)' }}>Solo pendientes</button>
                  </div>
                </div>
                <span style={{ fontSize: 11, color: '#475569' }}>
                  {nSel > 0 ? `${nSel} ítem${nSel !== 1 ? 's' : ''} marcado${nSel !== 1 ? 's' : ''}` : 'Sin selección'}
                </span>
              </div>

              {loadingItems && <div style={{ textAlign: 'center', padding: 28, color: '#64748b', fontSize: 13 }}>Cargando artículos...</div>}

              {!loadingItems && (
                <div style={{ border: '1px solid rgba(255,255,255,0.07)', borderRadius: 9, overflowX: 'auto', background: 'rgba(0,0,0,0.12)', minHeight: 80 }}>
                  <table className="rq-articles" style={{ minWidth: 920 }}>
                    <thead>
                      <tr>
                        <th style={{ width: 36, textAlign: 'center' }}>
                          <input type="checkbox" checked={todosSeleccionados} onChange={toggleAll}
                            title="Seleccionar todos los pendientes"
                            style={{ cursor: 'pointer', accentColor: '#3b82f6', width: 13, height: 13 }} />
                        </th>
                        <th style={{ width: 32 }}>#</th>
                        <th style={{ width: 108 }}>Código</th>
                        <th>Artículo</th>
                        <th style={{ width: 64, textAlign: 'center' }}>U.M.</th>
                        <th style={{ width: 90, textAlign: 'center' }}>RQ#</th>
                        <th style={{ width: 80, textAlign: 'right' }}>Cant. RQ</th>
                        <th style={{ width: 86, textAlign: 'right' }}>GR Emitida</th>
                        <th style={{ width: 80, textAlign: 'right' }}>Pendiente</th>
                        <th style={{ width: 105, textAlign: 'right' }}>A despachar ✎</th>
                      </tr>
                    </thead>
                    <tbody>
                      {itemsVisibles.length === 0 && (
                        <tr><td colSpan={10} className="rq-empty">
                          {soloPendientes ? 'No hay artículos pendientes de despacho.' : 'Este requerimiento no tiene artículos.'}
                        </td></tr>
                      )}
                      {itemsVisibles.map((it, i) => {
                        const pendiente     = parseFloat(it.cantidad_pendiente) || 0;
                        const grEmitida     = parseFloat(it.cantidad_gr)        || 0;
                        const totalRq       = parseFloat(it.cantidad)           || 0;
                        const agotado       = pendiente <= 0;
                        const checked       = it.id_detalle in seleccionados;
                        const cantDespachar = checked ? (parseFloat(seleccionados[it.id_detalle]) || 0) : 0;
                        const rqLabel = rqSeleccionado ? String(rqSeleccionado.numero).padStart(6, '0') : null;

                        return (
                          <tr key={it.id_detalle} style={{ opacity: agotado ? 0.4 : 1, background: checked ? 'rgba(59,130,246,0.07)' : 'transparent' }}>
                            <td style={{ textAlign: 'center', padding: '4px 6px' }}>
                              {agotado
                                ? <span title="Completamente despachado" style={{ fontSize: 14, color: '#22c55e' }}>✓</span>
                                : <input type="checkbox" checked={checked} onChange={() => toggleItem(it)} style={{ cursor: 'pointer', accentColor: '#3b82f6', width: 14, height: 14 }} />}
                            </td>
                            <td className="rq-td-num">{i + 1}</td>
                            <td style={{ padding: '4px 8px', color: '#94a3b8', fontFamily: 'monospace', fontSize: 10.5, whiteSpace: 'nowrap' }}>
                              {it.articulo_codigo || '—'}
                            </td>
                            <td style={{ padding: '4px 8px', minWidth: 160 }}>
                              <div style={{ fontSize: 12, color: '#e2e8f0', fontWeight: 500 }}>{it.articulo_descripcion || '—'}</div>
                            </td>
                            <td style={{ textAlign: 'center', padding: '4px 6px', color: '#64748b', fontSize: 11 }}>{it.unidad_codigo || '—'}</td>
                            <td style={{ textAlign: 'center', padding: '4px 6px' }}>
                              {rqLabel
                                ? <span style={{ fontSize: 10.5, fontWeight: 700, color: '#60a5fa', background: 'rgba(96,165,250,0.1)', border: '1px solid rgba(96,165,250,0.25)', borderRadius: 5, padding: '2px 6px', whiteSpace: 'nowrap' }}>{rqLabel}</span>
                                : <span style={{ color: '#374151', fontSize: 10 }}>—</span>}
                            </td>
                            <td style={{ textAlign: 'right', padding: '4px 8px', color: '#cbd5e1', fontVariantNumeric: 'tabular-nums', fontSize: 11.5 }}>{fmtN(totalRq)}</td>
                            <td style={{ textAlign: 'right', padding: '4px 8px', color: grEmitida > 0 ? '#a78bfa' : '#475569', fontVariantNumeric: 'tabular-nums', fontSize: 11.5 }}>{fmtN(grEmitida)}</td>
                            <td style={{ textAlign: 'right', padding: '4px 8px', color: agotado ? '#22c55e' : '#e2e8f0', fontWeight: 600, fontVariantNumeric: 'tabular-nums', fontSize: 11.5 }}>{fmtN(pendiente)}</td>
                            <td style={{ padding: '3px 6px' }}>
                              {checked ? (
                                <input
                                  type="number" min="0.0001" step="0.0001" max={pendiente}
                                  value={cantDespachar}
                                  onChange={e => setCantidadItem(it.id_detalle, parseFloat(e.target.value) || 0)}
                                  style={{
                                    width: '100%', height: 28, padding: '0 7px', boxSizing: 'border-box',
                                    textAlign: 'right', fontSize: 12, fontFamily: 'Inter, sans-serif',
                                    background: cantDespachar > pendiente ? 'rgba(239,68,68,0.12)' : 'rgba(59,130,246,0.08)',
                                    border: `1px solid ${cantDespachar > pendiente ? 'rgba(239,68,68,0.4)' : 'rgba(59,130,246,0.3)'}`,
                                    borderRadius: 6, color: '#e2e8f0', outline: 'none',
                                  }}
                                  onFocus={e => { e.target.style.borderColor = '#3b82f6'; e.target.style.boxShadow = '0 0 0 2px rgba(59,130,246,0.15)'; }}
                                  onBlur={e  => { e.target.style.borderColor = cantDespachar > pendiente ? 'rgba(239,68,68,0.4)' : 'rgba(59,130,246,0.3)'; e.target.style.boxShadow = 'none'; }}
                                />
                              ) : (
                                <div style={{ textAlign: 'right', padding: '0 7px', color: '#374151', fontSize: 12 }}>—</div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

          </div>
        </div>

        {/* ── Footer ── */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px', borderTop: '1px solid rgba(255,255,255,0.07)', background: 'rgba(0,0,0,0.15)', flexShrink: 0 }}>
          <button className="btn btn-secondary" onClick={() => setView('list')} disabled={saving}>
            ← Volver a lista
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {nSel > 0 && (
              <span style={{ fontSize: 12, color: '#64748b', fontFamily: 'Inter, sans-serif' }}>
                {nSel} artículo{nSel !== 1 ? 's' : ''} seleccionado{nSel !== 1 ? 's' : ''}
              </span>
            )}
            <button
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={saving || loadingCat || nSel === 0}
            >
              {saving ? 'Guardando...' : 'Generar GR'}
            </button>
          </div>
        </div>

      </div>
    </Layout>
  );
}
